import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Modal, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const Units = () => {
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [editId, setEditId] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post("/unit_list.php", {});
      
      if (response.data.head.code === 200) {
        setUnits(response.data.body);
      } else {
        setError(response.data.head.msg || "Failed to fetch units");
      }
    } catch (err) {
      console.error("Error fetching units:", err);
      setError("Failed to load units. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const handleShow = () => {
    setUnitName("");
    setEditId(null);
    setModalError(null);
    setShow(true);
  };

  const handleClose = () => {
    setUnitName("");
    setEditId(null);
    setModalError(null);
    setShow(false);
  };

  const handleEdit = (unit) => {
    setUnitName(unit.name);
    setEditId(unit.id);
    setModalError(null);
    setShow(true);
  };

  const handleSave = async () => {
    if (!unitName.trim()) {
      setModalError("Unit name is required");
      return;
    }

    setModalLoading(true);
    setModalError(null);

    try {
      if (editId) {
        // Update existing unit
        const response = await api.post("/unit_edit.php", {
          id: editId,
          name: unitName
        });
        
        if (response.data.head.code === 200) {
          // Update in local state
          setUnits(units.map((unit) =>
            unit.id === editId ? { ...unit, name: unitName } : unit
          ));
          handleClose();
          alert("Unit updated successfully!");
        } else {
          setModalError(response.data.head.msg || "Failed to update unit");
        }
      } else {
        // Add new unit
        const response = await api.post("/unit_add.php", {
          name: unitName
        });
        
        if (response.data.head.code === 200) {
          const newUnit = {
            id: response.data.body.unit_id,
            name: unitName
          };
          setUnits([...units, newUnit]);
          handleClose();
          alert("Unit added successfully!");
        } else {
          setModalError(response.data.head.msg || "Failed to add unit");
        }
      }
    } catch (err) {
      console.error("Error saving unit:", err);
      setModalError("Failed to save unit. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  // Delete unit handler with API
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
      return;
    }

    try {
      const response = await api.post("/unit_delete.php", { id });
      
      if (response.data.head.code === 200) {
        setUnits(units.filter((unit) => unit.id !== id));
        alert("Unit deleted successfully!");
      } else {
        alert("Failed to delete unit: " + response.data.head.msg);
      }
    } catch (err) {
      console.error("Error deleting unit:", err);
      alert("Failed to delete unit. Please try again.");
    }
  };

  // Filter units based on search
  const filteredUnits = units.filter((unit) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      String(unit.id).toLowerCase().includes(query) ||
      (unit.name && unit.name.toLowerCase().includes(query))
    );
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentUnits,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredUnits, 10);

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Units Management</h2>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading units...</p>
        </div>
      </Container>
    );
  }
{/*
  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Units Management</h2>
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchUnits}>
            Retry
          </button>
        </div>
      </Container>
    );
  }
  */}

  return (
    <>
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Units Management</h2>
        
        {/* Search and Add Unit Section */}
        <Row className="mb-3 align-items-center">
          <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
            <Form.Control
              placeholder="Search by unit name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>

          <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
            <Button className="btn-top" onClick={handleShow}>
              <FaPlus className="me-2" /> Add Unit
            </Button>
          </Col>
        </Row>

        {/* Table Section */}
        <div className="table-pagination-wrapper">
          <div className="table-pagination-content">
            <Table bordered responsive className="table-striped customer-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Unit Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUnits.map((unit, index) => (
                  <tr key={unit.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{unit.name}</td>
                    <td>
                      <div className="action-icons">
                        <FaEdit
                          className="edit-icon me-2"
                          onClick={() => handleEdit(unit)}
                          title="Edit Unit"
                          style={{ cursor: "pointer" }}
                        />
                        <FaTrash
                          className="delete-icon"
                          title="Delete Unit"
                          onClick={() => handleDelete(unit.id, unit.name)}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {currentUnits.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center">
                      {search ? "No units found matching your search" : "No units found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </Container>

      {/* Modal for Add/Edit Unit */}
      <Modal show={show} onHide={handleClose} centered className="department-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Unit" : "Add Unit"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && (
            <div className="alert alert-danger" role="alert">
              {modalError}
            </div>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Unit Name</Form.Label>
              <Form.Control
                type="text"
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                placeholder="Enter unit name"
                disabled={modalLoading}
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button 
                className="btn-submit btn" 
                onClick={handleSave}
                disabled={modalLoading || !unitName.trim()}
              >
                {modalLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {editId ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  editId ? "Update" : "Submit"
                )}
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleClose}
                disabled={modalLoading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Units;